import { colors } from '@/styles';
import { isPlaceholderItem, PLACEHOLDER_ITEM_STATUS } from '@/utilities/forwardStocking';
import { RECEIVED_STATUSES, STORED_STATUSES, MANIFEST_STATUSES } from '@/constants/manifestStatuses';
import { canMarkReceived, isPickedStockUnit } from '@/components/InventoryUnit/utils';
import {
    hasOpenException,
    getItemIdDisplay,
    getSerialDisplay,
    getBarcodeDisplay,
    getPalletDisplay,
    getLocationDisplay,
    getSkuDescription,
    getOrderPallets,
    uniformReceivedStatus,
    consolidatableItems,
    isReceivable,
    orderHasReceived,
    orderPastReceiving,
    formatQty,
    allPlaceholdersSelected,
    PLACEHOLDER_PRINT_BLOCKED,
    forwardStockingLocked,
    FORWARD_STOCKING_UNRECEIVE_BLOCKED,
} from '../utils';

const CATEGORIES = [
    { key: 'RECEIVED', label: 'Received', color: colors.greens.primary },
    { key: 'NOT_RECEIVED', label: 'Not Received', color: colors.oranges.primary },
    { key: 'EXCEPTION', label: 'Exception', color: colors.reds.primary },
];
const COLOR = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.color]));

const DASH = { label: '—', color: colors.greys.primary };

const classify = (item) => {
    if (hasOpenException(item)) return 'EXCEPTION';
    if (RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)) return 'RECEIVED';
    // Receiving is history: a unit picked off the shelf was received.
    if (isPickedStockUnit(item)) return 'RECEIVED';
    return 'NOT_RECEIVED';
};

// Per-child status pill. Stored location lives in its own Location column, not the status label.
const childStatus = (item, order) => {
    if (isPlaceholderItem(item, order)) return PLACEHOLDER_ITEM_STATUS;
    const key = classify(item);
    if (key === 'EXCEPTION') return { label: 'Exception', color: COLOR.EXCEPTION };
    if (isPickedStockUnit(item)) return { label: 'Received · Picked', color: COLOR.RECEIVED };
    // The pallet can't say whether a unit that left the shelf was received.
    if (key === 'NOT_RECEIVED' && !canMarkReceived(item)) return DASH;
    if (key === 'NOT_RECEIVED') return { label: 'Not Received', color: COLOR.NOT_RECEIVED };
    return { label: 'Received', color: COLOR.RECEIVED };
};

export const inboundConfig = {
    statusModel: {
        categories: CATEGORIES,
        classify,
        chip: (counts, qty) => {
            if (counts.EXCEPTION > 0) return { label: 'Exception', key: 'EXCEPTION' };
            if (counts.NOT_RECEIVED === 0 && qty > 0) return { label: 'Received', key: 'RECEIVED' };
            return { label: `${counts.RECEIVED} / ${qty}`, key: 'NOT_RECEIVED' };
        },
        statusText: (counts, qty) => {
            if (counts.EXCEPTION > 0) {
                return `${counts.EXCEPTION} exception${counts.EXCEPTION > 1 ? 's' : ''} · ${counts.RECEIVED}/${qty}`;
            }
            if (counts.NOT_RECEIVED > 0) return `${counts.RECEIVED}/${qty} received`;
            return `All ${qty} received`;
        },
    },

    legend: CATEGORIES,

    // Header progress bars (first entry drives the header status pill / deriveStatus). A `category`
    // bar counts a statusModel segment; a `statuses` bar counts units at a pallet-status milestone.
    progress: [
        { key: 'received', category: 'RECEIVED', label: 'Received Progress', verb: 'received' },
        { key: 'stored', statuses: STORED_STATUSES, label: 'Stored Progress', verb: 'stored' },
    ],

    deriveStatus: ({ inProgress, total }) => {
        if (total > 0 && inProgress >= total) return MANIFEST_STATUSES.RECEIVED;
        if (inProgress > 0) return MANIFEST_STATUSES.IN_PROGRESS;
        return MANIFEST_STATUSES.NOT_RECEIVED;
    },

    extraHeaderFields: () => [],

    childColumns: [
        { key: 'sku', header: 'SKU / Description', width: '1.4fr', variant: 'text', sortable: true, filterable: true, get: getSkuDescription },
        { key: 'qty', header: 'Qty', width: '0.4fr', variant: 'text', sortable: true, filterable: true, get: (item) => formatQty(item.quantity ?? 1) },
        { key: 'itemId', header: 'Line ID', width: '0.95fr', variant: 'id', sortable: true, filterable: true, get: getItemIdDisplay },
        { key: 'serial', header: 'Serial', width: '1.1fr', variant: 'text', sortable: true, filterable: true, get: getSerialDisplay },
        { key: 'barcode', header: 'Barcode / QR', width: '1.15fr', variant: 'text', sortable: true, filterable: true, get: getBarcodeDisplay },
        { key: 'pallet', header: 'Pallet ID', width: '0.9fr', variant: 'id', sortable: true, filterable: true, get: (item) => getPalletDisplay(item.pallet) || '—' },
        { key: 'location', header: 'Location', width: '0.9fr', variant: 'text', sortable: true, filterable: true, get: getLocationDisplay },
        { key: 'received', header: 'Received', width: '0.9fr', variant: 'pill', sortable: true, filterable: true, get: childStatus, getValue: (item, order) => childStatus(item, order).label },
    ],

    // ctx = { subject: manifest, row: order, child: item }
    rowButtons: (actions) => [
        {
            key: 'receive',
            label: '✓ Mark received',
            color: colors.greens.primary,
            visible: ({ child }) =>
                !RECEIVED_STATUSES.includes(child?.pallet?.warehouse_status) && canMarkReceived(child),
            onClick: ({ child }) => actions.receive([child]),
        },
        {
            key: 'partial-receive',
            label: '✂ Partially receive',
            visible: ({ child }) =>
                (child?.quantity || 1) > 1 &&
                !RECEIVED_STATUSES.includes(child?.pallet?.warehouse_status) &&
                canMarkReceived(child),
            onClick: ({ row, child }) => actions.partialReceive({ item: child, order: row }),
        },
        {
            key: 'assign',
            label: '⌒ Assign location',
            // Only after an item is received — receiving is what writes the OMS wh_events; assigning a
            // location never marks an item received (avoids the WMS/OMS divergence).
            visible: ({ child }) => RECEIVED_STATUSES.includes(child?.pallet?.warehouse_status),
            onClick: ({ child }) => actions.assignLocation([child]),
        },
        {
            key: 'rename',
            label: '✎ Rename pallet',
            visible: ({ child }) => !!child?.pallet,
            onClick: ({ child }) => actions.renamePallet(child),
        },
        {
            key: 'history',
            label: '🕘 Pallet history',
            visible: ({ child }) => !!child?.pallet?.pallet_id,
            onClick: ({ child }) => actions.palletLog(child.pallet),
        },
        {
            key: 'flag',
            label: '⚑ Flag exception',
            color: colors.reds.primary,
            onClick: ({ child }) => actions.flagException([child]),
        },
        {
            key: 'resolve',
            label: '✓ Resolve exception',
            color: colors.greens.primary,
            visible: ({ child }) => hasOpenException(child),
            onClick: ({ row, child }) => actions.resolveException({ order: row, item: child }),
        },
    ],

    parentButtons: (actions) => [
        // Consolidation is a bulk action now (any selection, any orders) — see bulkButtons.
        { key: 'dock', label: '⇄ Relocate to dock', onClick: ({ row }) => actions.relocateToDock(row.manifestItems || []) },
        {
            key: 'history',
            label: '🕘 Pallet history',
            visible: ({ row }) => getOrderPallets(row.manifestItems || []).length === 1,
            onClick: ({ row }) => actions.palletLog(getOrderPallets(row.manifestItems || [])[0]),
        },
        {
            key: 'unreceive',
            label: '⤺ Un-receive order',
            color: colors.reds.primary,
            visible: ({ row }) => orderHasReceived(row),
            disabled: ({ row }) => orderPastReceiving(row) || forwardStockingLocked(row),
            // Only explains the forward-stocking block. orderPastReceiving is a pre-existing gate
            // with its own meaning ("un-stage it first"), and labelling it with this reason would
            // send the user after the wrong thing.
            disabledReason: ({ row }) =>
                !orderPastReceiving(row) && forwardStockingLocked(row) ? FORWARD_STOCKING_UNRECEIVE_BLOCKED : '',
            onClick: ({ row }) => actions.unreceive(row),
        },
    ],

    // ctx = { subject: manifest, selected: [items], clearSelection }
    bulkButtons: (actions) => [
        {
            key: 'receive',
            label: '✓ Mark received',
            variant: 'primary',
            visible: ({ selected }) => selected.length > 0,
            // Nothing receivable left would be a no-op click.
            disabled: ({ selected }) => !selected.some(isReceivable),
            onClick: ({ selected }) => actions.receive(selected),
        },
        {
            key: 'assign',
            label: '⌒ Assign location',
            variant: 'outlined',
            color: colors.blues.primary,
            // Same gate as the row action — not-received items are filtered out by actions.assignLocation.
            visible: ({ selected }) =>
                selected.some((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)),
            onClick: ({ selected }) => actions.assignLocation(selected),
        },
        // Exceptions can only be raised one item at a time.
        {
            key: 'flag',
            label: '⚑ Flag exception',
            variant: 'outlined',
            color: colors.reds.primary,
            visible: ({ selected }) => selected.length === 1,
            onClick: ({ selected }) => actions.flagException(selected),
        },
        // Any selection, across orders → one shared pallet.
        {
            key: 'consolidate',
            label: '⊟ Consolidate',
            variant: 'outlined',
            color: colors.blues.primary,
            // One shared pallet means one shared status, so the selection must already agree.
            visible: ({ selected }) => {
                const items = consolidatableItems(selected);
                return items.length > 1 && uniformReceivedStatus(items);
            },
            onClick: ({ selected }) => actions.consolidate(selected),
        },
        {
            key: 'print-item-labels',
            label: '🖨 Print item labels',
            variant: 'outlined',
            visible: ({ selected }) => selected.length > 0,
            disabled: allPlaceholdersSelected,
            disabledReason: (ctx) => (allPlaceholdersSelected(ctx) ? PLACEHOLDER_PRINT_BLOCKED : ''),
            onClick: ({ selected }) => actions.printItemLabels(selected),
        },
        {
            key: 'print-pallet-labels',
            label: '🖨 Print pallet labels',
            variant: 'outlined',
            // Only palletized items have a pallet label to print.
            visible: ({ selected }) => selected.some((item) => item?.pallet?.pallet_id),
            onClick: ({ selected }) => actions.printPalletLabels(selected),
        },
    ],
};
